import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api'

/**
 * Billing: current plan, this-month usage, and the plan catalogue. Upgrading raises a request
 * the operator confirms (no card processor wired yet); the layout is checkout-ready for when
 * one is.
 */
export function Billing() {
  const { data, isLoading } = useQuery({ queryKey: ['billing'], queryFn: api.billing.get })
  const [done, setDone] = useState<string | null>(null)
  const [error, setError] = useState<ApiError | null>(null)
  const upgrade = useMutation({
    mutationFn: (plan: string) => api.billing.requestUpgrade(plan),
    onSuccess: (r) => { setDone(r.message); setError(null) },
    onError: (e) => setError(e as ApiError),
  })

  if (isLoading) return <div className="skeleton" style={{ height: 260 }} />

  return (
    <div>
      <h1>Billing &amp; plans</h1>
      <p className="lede">You are on the <strong>{data?.current_plan ?? '—'}</strong> plan.</p>

      <div className="stats" style={{ marginBottom: 24 }}>
        <div className="rate ok"><div className="rate-label">Accepted this month</div><div className="rate-value">{(data?.usage_this_month.accepted ?? 0).toLocaleString()}</div></div>
        <div className="rate ok"><div className="rate-label">Delivered this month</div><div className="rate-value">{(data?.usage_this_month.delivered ?? 0).toLocaleString()}</div></div>
        <div className="rate"><div className="rate-label">Free allowance left</div><div className="rate-value">{(data?.free_allowance_remaining ?? 0).toLocaleString()}</div></div>
      </div>

      {done && <div className="alert ok" style={{ marginBottom: 16 }}>{done}</div>}
      {error && <div className="alert error" style={{ marginBottom: 16 }}>{error.message}</div>}

      <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
        {(data?.plans ?? []).map(p => {
          const current = p.name === data?.current_plan
          return (
            <div key={p.name} className="card" style={{ flex: 1, minWidth: 240, borderColor: current ? 'var(--brand-500)' : undefined }}>
              <div className="spread" style={{ alignItems: 'center' }}>
                <h2 style={{ marginTop: 0, marginBottom: 0, textTransform: 'capitalize' }}>{p.name}</h2>
                {current && <span className="badge ok">current</span>}
                {p.is_free && !current && <span className="badge neutral">free</span>}
              </div>
              <ul className="small" style={{ marginTop: 12, lineHeight: 1.9 }}>
                <li>{p.daily_send_limit.toLocaleString()} emails / day</li>
                <li>{p.hourly_send_limit.toLocaleString()} / hour</li>
                <li>{p.max_domains} domains</li>
                <li>{p.max_recipients_per_message.toLocaleString()} recipients / message</li>
                <li>{p.tracking_allowed ? 'Open & click tracking' : 'No tracking'}</li>
                <li>{p.dedicated_ip_allowed ? 'Dedicated IP available' : 'Shared IPs'}</li>
                <li>{p.inbound_retention_days}-day inbound retention</li>
              </ul>
              {!current && (
                <button style={{ width: '100%' }} disabled={upgrade.isPending}
                        onClick={() => upgrade.mutate(p.name)}>
                  {upgrade.isPending ? 'Requesting…' : `Switch to ${p.name}`}
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
